import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDownload, faXmark } from "@fortawesome/sharp-solid-svg-icons"
import { Box, Button, IconButton, styled } from "@mui/material"
import {
  useListPlatformsBiomeshPlatformListGet,
  useListSensorsBiomeshSensorListGet,
} from "@phc/common/gen/threatAnalyticsGen"
import { useCallback, useMemo } from "react"
import { extraColors } from "../../utils/theme"
import {
  matchesSensorFilters,
  useBioMeshUIState,
} from "./BioMeshUIStateContext"
import { exportSensorsToCSV } from "./exportSensors"

const ExportButton = styled(Button)({
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: extraColors.status.navyLight,
  color: extraColors.dark,
  "&:hover": {
    backgroundColor: extraColors.status.navy,
  },
  "&.Mui-disabled": {
    backgroundColor: extraColors.disabled,
    color: extraColors.dark,
  },
})

interface ExportVisibleButtonProps {
  clearLasso: () => void
}

export const ExportVisibleButton = ({
  clearLasso,
}: ExportVisibleButtonProps) => {
  const { filters, selectedNodeIds, clearSelection, setIsSelecting } =
    useBioMeshUIState()
  const { data: sensors } = useListSensorsBiomeshSensorListGet({
    limit: 10_000,
  })
  const { data: platforms } = useListPlatformsBiomeshPlatformListGet()

  const hasSelection = selectedNodeIds.size > 0

  const selectedSensors = useMemo(() => {
    if (!sensors || !hasSelection) return []
    return sensors.filter(
      s => s.platform_id != null && selectedNodeIds.has(s.platform_id)
    )
  }, [sensors, selectedNodeIds, hasSelection])

  const handleExportVisible = useCallback(() => {
    if (!sensors || !platforms) return
    const visible = sensors.filter(s => matchesSensorFilters(filters, s))
    exportSensorsToCSV(visible, platforms)
  }, [sensors, platforms, filters])

  const handleExportSelected = useCallback(() => {
    if (!platforms) return
    exportSensorsToCSV(selectedSensors, platforms, "biomesh-sensors-selected")
  }, [selectedSensors, platforms])

  const handleClear = useCallback(() => {
    clearSelection()
    setIsSelecting(false)
    clearLasso()
  }, [clearSelection, setIsSelecting, clearLasso])

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <ExportButton
        variant="contained"
        size="small"
        startIcon={<FontAwesomeIcon icon={faDownload} />}
        onClick={hasSelection ? handleExportSelected : handleExportVisible}
        disabled={
          hasSelection ? selectedSensors.length === 0 : !sensors?.length
        }
      >
        {hasSelection
          ? `Export ${selectedSensors.length} sensor${selectedSensors.length !== 1 ? "s" : ""}`
          : "Export Visible"}
      </ExportButton>

      {hasSelection && (
        <IconButton
          onClick={handleClear}
          size="small"
          aria-label="Clear selection"
          sx={{
            color: extraColors.hint,
            "&:hover": { color: extraColors.white },
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      )}
    </Box>
  )
}
